import React, { useEffect, useMemo, useState } from 'react';
import PrimaryCurrencyField from './PrimaryCurrencyField';
import SearchableSelect from '../ui/SearchableSelect';
import Button from '../ui/Button';
import LoadErrorBox from '../ui/LoadErrorBox';
import { useSettings } from '../../contexts/SettingsContext';
import { useRole } from '../../hooks/useRole';
import { useNotifications } from '../NotificationProvider';
import { errorMessageOf } from '../../utils/apiErrors';
import { localeOptions, phoneRegionOptions, timezoneOptions } from '../../utils/regions';
import type { ProjectCurrency } from '../../utils/currency';
import type { ProjectChoiceKey, ProjectChoicesChange } from '../../services/SettingsService';

const MISSING_LABEL: Record<ProjectChoiceKey, string> = {
  currency: 'main currency',
  timezone: 'timezone',
  locale: 'language and number format',
  phone_region: 'phone country',
};

/**
 * The four choices a project makes for itself: main currency, timezone,
 * language (number and date format) and phone country. Nothing is preselected;
 * until chosen they stay empty and are named as still to choose. The currency
 * is read-only once the project holds money (the API says so with
 * `currencyLocked`). Only an owner can save.
 */
export default function ProjectChoicesCard() {
  const { projectChoices, projectChoicesError, saveProjectChoices, refreshProjectChoices } = useSettings();
  const { isOwner } = useRole();
  const { showError, showSuccess } = useNotifications();

  const [currency, setCurrency] = useState<ProjectCurrency>(projectChoices.currency);
  const [timezone, setTimezone] = useState(projectChoices.timezone);
  const [locale, setLocale] = useState(projectChoices.locale);
  const [phoneRegion, setPhoneRegion] = useState(projectChoices.phoneRegion);
  const [saving, setSaving] = useState(false);

  // Follow the saved values whenever they change (first load, a save, a re-read after a refusal).
  useEffect(() => {
    setCurrency(projectChoices.currency);
    setTimezone(projectChoices.timezone);
    setLocale(projectChoices.locale);
    setPhoneRegion(projectChoices.phoneRegion);
  }, [projectChoices]);

  const timezones = useMemo(timezoneOptions, []);
  const locales = useMemo(localeOptions, []);
  const regions = useMemo(phoneRegionOptions, []);

  const change = useMemo(() => {
    const out: ProjectChoicesChange = {};
    if (currency && currency !== projectChoices.currency) out.currency = currency;
    if (timezone && timezone !== projectChoices.timezone) out.timezone = timezone;
    if (locale && locale !== projectChoices.locale) out.locale = locale;
    if (phoneRegion && phoneRegion !== projectChoices.phoneRegion) out.phoneRegion = phoneRegion;
    return out;
  }, [currency, timezone, locale, phoneRegion, projectChoices]);
  const dirty = Object.keys(change).length > 0;

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await saveProjectChoices(change);
      showSuccess('Project settings saved.');
    } catch (err) {
      showError(errorMessageOf(err, "Couldn't save the project settings. Please try again."));
      // A refused currency change means the project now holds money: re-read so the field locks.
      void refreshProjectChoices();
    } finally {
      setSaving(false);
    }
  };

  if (projectChoicesError) {
    return (
      <div className="settings-card">
        <LoadErrorBox what="project settings" error={projectChoicesError} onRetry={() => { void refreshProjectChoices(); }} />
      </div>
    );
  }

  const readOnly = !isOwner;
  return (
    <div className="settings-card">
      <h4 className="settings-card-title">Project settings</h4>
      <p className="settings-card-hint">
        Chosen once for this project. Nothing is assumed for you: until you choose, amounts say "currency not set".
      </p>
      {projectChoices.missing.length > 0 && (
        <p className="form-hint" role="status">
          Still to choose: {projectChoices.missing.map(k => MISSING_LABEL[k]).join(', ')}.
        </p>
      )}
      <div className="settings-grid">
        <PrimaryCurrencyField
          value={currency}
          locked={projectChoices.currencyLocked}
          readOnly={readOnly}
          onChange={setCurrency}
        />
        <div className="form-group">
          <label>Timezone</label>
          <SearchableSelect value={timezone} placeholder="Select a timezone" options={timezones} onChange={setTimezone} disabled={readOnly} />
        </div>
        <div className="form-group">
          <label>Language and number format</label>
          <SearchableSelect value={locale} placeholder="Select a language" options={locales} onChange={setLocale} disabled={readOnly} />
        </div>
        <div className="form-group">
          <label>Phone country</label>
          <SearchableSelect value={phoneRegion} placeholder="Select a country" options={regions} onChange={setPhoneRegion} disabled={readOnly} />
          <p className="form-hint">Used to read phone numbers typed without a country code.</p>
        </div>
      </div>
      {readOnly ? (
        <p className="form-hint">Only an owner can change these.</p>
      ) : (
        <Button variant="primary" onClick={handleSave} disabled={!dirty} loading={saving}>Save</Button>
      )}
    </div>
  );
}
